// ─── PDF de cierre de caja ───────────────────────────────────────────────────
// Se usa tanto al cerrar el turno (CierreCaja) como al reimprimir un cierre
// histórico desde el Informe Financiero, para que ambos documentos sean
// idénticos. Los montos se leen del turno ya cerrado.

import { jsPDF } from 'jspdf';
import { CLP, fmtFechaHora } from '../utils/finance';

/**
 * Genera el PDF de cierre de caja de un turno.
 *
 * @param {object} turno - Documento de turnosCaja (con los totales del cierre).
 * @param {object} opts
 * @param {object} opts.branch - Sucursal para el encabezado.
 * @param {boolean} opts.autoPrint - Abre el diálogo de impresión (por defecto true).
 * @param {boolean} opts.reimpresion - Marca el documento como reimpresión.
 */
export function generateCierreCajaPDF(turno, { branch = {}, autoPrint = true, reimpresion = false } = {}) {
  if (!turno) throw new Error('Turno no disponible para generar el cierre.');

  const totalEfectivo = Number(turno.totalEfectivo) || 0;
  const totalDebito = Number(turno.totalDebito) || 0;
  const totalCredito = Number(turno.totalCredito) || 0;
  const totalTransferencia = Number(turno.totalTransferencia) || 0;
  const totalOtros = Number(turno.totalOtros) || 0;
  const totalBruto = Number(turno.totalBruto) || 0;
  const montoApertura = Number(turno.montoApertura) || 0;
  const cuadreEfectivo = Number(turno.cuadreEfectivo) || 0;
  const diferencia = Number(turno.diferencia) || 0;
  const efectivoEsperado = montoApertura + totalEfectivo;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const m = 18;
  let y = m;

  doc.setFillColor(26, 58, 92); doc.rect(0, 0, 210, 30, 'F');
  doc.setTextColor(255, 255, 255); doc.setFont('helvetica', 'bold'); doc.setFontSize(15);
  doc.text(branch.name || 'Hospital Clínico Veterinario UDLA', m, 13);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
  const contactLine = [branch.address, branch.phone && `Tel: ${branch.phone}`, branch.email].filter(Boolean).join('  ·  ');
  if (contactLine) doc.text(contactLine, m, 18);
  if (branch.website) { doc.setFontSize(7.5); doc.text(branch.website, m, 22); }
  doc.setFont('helvetica', 'bold'); doc.setFontSize(13);
  doc.text('Cierre de Caja', 210 - m, 13, { align: 'right' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  doc.text(`${turno.cajaNombre || ''} · Turno ${turno.fechaTurno || ''}`, 210 - m, 19, { align: 'right' });

  y = 40;
  doc.setTextColor(30, 30, 30); doc.setFontSize(9);
  if (reimpresion) {
    doc.setFont('helvetica', 'bold');
    doc.text(`REIMPRESIÓN · ${fmtFechaHora(new Date())}`, m, y);
    doc.setFont('helvetica', 'normal');
    y += 6;
  }
  doc.text(`Apertura: ${fmtFechaHora(turno.aperturaAt)}`, m, y); y += 5;
  doc.text(`Cierre: ${fmtFechaHora(turno.cierreAt)}`, m, y); y += 5;
  doc.text(`Cajero apertura: ${turno.usuarioAperturaNombre || ''}`, m, y); y += 5;
  doc.text(`Cajero cierre: ${turno.usuarioCierreNombre || ''}`, m, y); y += 8;

  doc.setFont('helvetica', 'bold'); doc.text('Ventas por medio de pago', m, y); y += 6;
  doc.setFont('helvetica', 'normal');
  [
    ['Efectivo', totalEfectivo], ['Débito', totalDebito], ['Crédito', totalCredito],
    ['Transferencia', totalTransferencia], ['Otros', totalOtros],
  ].forEach(([k, v]) => { doc.text(k, m, y); doc.text(CLP(v), 210 - m, y, { align: 'right' }); y += 5; });
  doc.setFont('helvetica', 'bold');
  doc.text('Total ventas', m, y); doc.text(CLP(totalBruto), 210 - m, y, { align: 'right' }); y += 5;
  doc.text(`N° transacciones: ${turno.conteoVentas || 0}`, m, y); y += 8;

  doc.setFont('helvetica', 'bold'); doc.text('Arqueo de efectivo', m, y); y += 6;
  doc.setFont('helvetica', 'normal');
  doc.text('Fondo de apertura', m, y); doc.text(CLP(montoApertura), 210 - m, y, { align: 'right' }); y += 5;
  doc.text('Efectivo esperado', m, y); doc.text(CLP(efectivoEsperado), 210 - m, y, { align: 'right' }); y += 5;
  doc.text('Efectivo contado', m, y); doc.text(CLP(cuadreEfectivo), 210 - m, y, { align: 'right' }); y += 5;
  doc.setFont('helvetica', 'bold');
  const difLabel = diferencia === 0 ? 'Cuadrado' : diferencia > 0 ? 'Sobrante' : 'Faltante';
  doc.text(`Diferencia (${difLabel})`, m, y); doc.text(CLP(diferencia), 210 - m, y, { align: 'right' }); y += 10;

  if (turno.observacionesCierre) {
    doc.setFont('helvetica', 'bold'); doc.text('Observaciones', m, y); y += 5;
    doc.setFont('helvetica', 'normal');
    doc.splitTextToSize(turno.observacionesCierre, 174).forEach((ln) => { doc.text(ln, m, y); y += 5; });
  }

  y += 20;
  doc.line(m, y, m + 70, y); doc.text('Firma cajero', m, y + 5);

  if (autoPrint) doc.autoPrint();
  doc.output('dataurlnewwindow');
}

export default generateCierreCajaPDF;
